import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';

// 定义数据类型
interface SimpleProduct {
  id: string;
  name: string;
  image: string;
  summary: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  image: string;
  products: SimpleProduct[];
}

interface CategoryData {
  categories: Category[];
}

const Services: React.FC = () => {
  const bannerImagePath = '/images/services/banner.webp';
  const { t, i18n } = useTranslation();
  
  // 状态管理
  const [selectedCategory, setSelectedCategory] = useState<string>('perfume');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // 处理产品卡片点击
  const handleProductClick = (productId: string) => {
    window.open(`#/product/${productId}`, '_blank');
  };
  
  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // 优先从 localStorage 读取语言设置（确保新标签页也能正确读取）
        const storedLang = localStorage.getItem('i18nextLng');
        const langFromStorage = storedLang ? storedLang.split('-')[0] : null;
        const langFromI18n = i18n.language?.split('-')[0] || 'zh';
        const currentLang = langFromStorage || langFromI18n;
        
        // 根据当前语言加载对应的产品分类数据
        let categoryResponse;
        if (currentLang === 'en') {
          // 尝试加载英文版本
          categoryResponse = await fetch('/data/product-category-en.json');
          if (!categoryResponse.ok) {
            // 如果英文版本不存在，回退到中文版本
            categoryResponse = await fetch('/data/product-category.json');
          }
        } else {
          // 加载中文版本
          categoryResponse = await fetch('/data/product-category.json');
        }
        
        const categoryData: CategoryData = await categoryResponse.json();
        setCategories(categoryData.categories);
        
        if (categoryData.categories.length > 0) {
          setSelectedCategory(categoryData.categories[0].id);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('加载数据失败:', error);
        setLoading(false);
      }
    };
    
    // 等待 i18n 初始化完成后再加载数据
    if (i18n.isInitialized) {
      loadData();
    } else {
      const initHandler = () => {
        loadData();
        i18n.off('initialized', initHandler);
      };
      i18n.on('initialized', initHandler);
    }

    // 监听语言变化事件
    const handleLanguageChanged = () => {
      loadData();
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);
  
  // 获取当前分类的产品
  const getCurrentCategoryProducts = () => {
    const currentCategory = categories.find(cat => cat.id === selectedCategory);
    return currentCategory?.products || [];
  };

  // 将 JSON ID 转换为 i18n 键（kebab-case to camelCase）
  const getCategoryI18nKey = (categoryId: string): string => {
    return categoryId.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  };

  // 获取翻译的分类名称
  const getCategoryName = (categoryId: string): string => {
    const i18nKey = getCategoryI18nKey(categoryId);
    return t(`products.categories.${i18nKey}.name`);
  };

  return (
    <main className="services-container">
      {/* 页面标题区域 */}
      <section className="services-hero-section">
        <div 
          className="services-hero-background"
          style={{
            backgroundImage: `url('${bannerImagePath}')`
          }}
        >
          <div className="services-hero-overlay">
            <div className="services-hero-container">
              <h1 className="services-hero-title">{t('services.title')}</h1>
              <p className="services-hero-subtitle">
                {t('services.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 业务模式区域 */}
      <section className="business-model-section">
        <div className="business-model-container">
          <h2 className="section-title">{t('services.businessModel.title')}</h2>
          <div className="business-model-grid">
            <div className="business-model-item business-model-left">
              <div className="business-model-left-section">
                <img 
                  src="/images/services/1.webp" 
                  alt={t('services.businessModel.oem.title')}
                  className="business-model-image"
                />
              </div>
              <div className="business-model-main">
                <div className="business-model-header">
                  <div className="business-model-number">01</div>
                  <h3 className="business-model-title">{t('services.businessModel.oem.title')}</h3>
                </div>
                <div className="business-model-content">
                  <p className="business-model-description">
                    {t('services.businessModel.oem.description')}
                  </p>
                  <div className="business-model-features">
                    {(t('services.businessModel.oem.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <span key={index} className="business-feature-badge">{feature}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="business-model-item business-model-right">
              <div className="business-model-left-section">
                <img 
                  src="/images/services/2.webp" 
                  alt={t('services.businessModel.odm.title')}
                  className="business-model-image"
                />
              </div>
              <div className="business-model-main">
                <div className="business-model-header">
                  <div className="business-model-number">02</div>
                  <h3 className="business-model-title">{t('services.businessModel.odm.title')}</h3>
                </div>
                <div className="business-model-content">
                  <p className="business-model-description">
                    {t('services.businessModel.odm.description')}
                  </p>
                  <div className="business-model-features">
                    {(t('services.businessModel.odm.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <span key={index} className="business-feature-badge">{feature}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="business-model-item business-model-left">
              <div className="business-model-left-section">
                <img 
                  src="/images/services/3.webp" 
                  alt={t('services.businessModel.private.title')}
                  className="business-model-image"
                />
              </div>
              <div className="business-model-main">
                <div className="business-model-header">
                  <div className="business-model-number">03</div>
                  <h3 className="business-model-title">{t('services.businessModel.private.title')}</h3>
                </div>
                <div className="business-model-content">
                  <p className="business-model-description">
                    {t('services.businessModel.private.description')}
                  </p>
                  <div className="business-model-features">
                    {(t('services.businessModel.private.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <span key={index} className="business-feature-badge">{feature}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="business-model-item business-model-right">
              <div className="business-model-left-section">
                <img 
                  src="/images/services/4.webp" 
                  alt={t('services.businessModel.sensory.title')}
                  className="business-model-image"
                />
              </div>
              <div className="business-model-main">
                <div className="business-model-header">
                  <div className="business-model-number">04</div>
                  <h3 className="business-model-title">{t('services.businessModel.sensory.title')}</h3>
                </div>
                <div className="business-model-content">
                  <p className="business-model-description">
                    {t('services.businessModel.sensory.description')}
                  </p>
                  <div className="business-model-features">
                    {(t('services.businessModel.sensory.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <span key={index} className="business-feature-badge">{feature}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 产品列表区域 */}
      <section className="product-categories-section">
        <div className="product-categories-container">
          <h2 className="section-title">{t('services.productList')}</h2>
          
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('services.loading')}</p>
            </div>
          ) : (
            <div className="product-categories-layout">
              {/* 左侧分类选择器 */}
              <div className="category-selector">
                <div className="category-selector-header">
                  <h3 className="category-selector-title">{t('services.productList')}</h3>
                </div>
                <div className="category-selector-list">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      className={`category-selector-item ${
                        selectedCategory === category.id ? 'active' : ''
                      }`}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      {getCategoryName(category.id)}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* 右侧产品列表 */}
              <div className="product-list-container">
                {/* 产品网格 */}
                <div className="product-grid">
                  {getCurrentCategoryProducts().length > 0 ? (
                    getCurrentCategoryProducts().map((product) => (
                      <div 
                        key={product.id} 
                        className="product-item"
                        onClick={() => handleProductClick(product.id)}
                      >
                        <div className="product-image">
                          <img 
                            src={product.image} 
                            alt={product.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className="product-placeholder hidden">
                            <span>{t('services.noImage')}</span>
                          </div>
                        </div>
                        <div className="product-content">
                          <h4 className="product-title">{product.name}</h4>
                          <p className="product-summary">{product.summary}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-products">
                      <p>{t('services.noProducts')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Services;
